import { useEffect, useRef } from "react";
import * as THREE from "three";

/** Procedural architecture: no external models, textures, tracking or network. */
export default function HeroWorld() {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return;
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    renderer.setClearColor(0x080c14, 0);
    element.append(renderer.domElement);
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x080c14, 0.028);
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    const world = new THREE.Group();
    scene.add(world);
    const materials: THREE.Material[] = [],
      geometries: THREE.BufferGeometry[] = [];
    const material = (color: number, metalness = 0.5) => {
      const value = new THREE.MeshStandardMaterial({
        color,
        metalness,
        roughness: 0.3,
      });
      materials.push(value);
      return value;
    };
    const stone = material(0x20364c),
      lightStone = material(0x50657b),
      orange = material(0xff7044);
    function box(
      x: number,
      y: number,
      z: number,
      w: number,
      h: number,
      d: number,
      mat: THREE.Material,
    ) {
      const geometry = new THREE.BoxGeometry(w, h, d);
      geometries.push(geometry);
      const mesh = new THREE.Mesh(geometry, mat);
      mesh.position.set(x, y, z);
      world.add(mesh);
      const edgeGeometry = new THREE.EdgesGeometry(geometry);
      geometries.push(edgeGeometry);
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x72c9dc,
        transparent: true,
        opacity: 0.18,
      });
      materials.push(lineMat);
      mesh.add(new THREE.LineSegments(edgeGeometry, lineMat));
      return mesh;
    }
    box(0, -0.5, 0, 11, 0.6, 8, stone);
    for (let i = 0; i < 6; i++)
      box(-4 + i * 0.85, -0.05 + i * 0.12, 3.2, 1, 0.22, 1.3, lightStone);
    for (let i = 0; i < 5; i++) {
      const z = 2.1 - i * 1.12;
      box(-1.5, 1.5, z, 0.25, 3.2, 0.25, lightStone);
      box(2.2, 1.5, z, 0.25, 3.2, 0.25, lightStone);
      box(0.35, 3.1, z, 3.95, 0.25, 0.25, lightStone);
    }
    box(-3.6, 0.9, -1, 2, 2.2, 2.8, stone);
    box(3.6, 0.35, -2.5, 1.6, 1.2, 1.6, orange);
    const coreGeometry = new THREE.IcosahedronGeometry(0.82, 0);
    geometries.push(coreGeometry);
    const core = new THREE.Mesh(coreGeometry, orange);
    core.position.set(0.35, 1.45, 0);
    world.add(core);
    const ringGeometry = new THREE.TorusGeometry(1.25, 0.014, 8, 80);
    geometries.push(ringGeometry);
    const ringMaterial = new THREE.MeshBasicMaterial({ color: 0x8be8ff });
    materials.push(ringMaterial);
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(core.position);
    ring.rotation.x = 0.6;
    world.add(ring);
    const grid = new THREE.GridHelper(40, 40, 0x2c6275, 0x142a3b);
    grid.position.y = -0.83;
    scene.add(grid);
    geometries.push(grid.geometry);
    materials.push(
      ...(Array.isArray(grid.material) ? grid.material : [grid.material]),
    );
    scene.add(new THREE.HemisphereLight(0xc4eaff, 0x132033, 2.5));
    const key = new THREE.DirectionalLight(0xb9e9ff, 4);
    key.position.set(3, 8, 5);
    scene.add(key);
    const rim = new THREE.PointLight(0xff693a, 45, 20);
    rim.position.set(-3, 3, 0);
    scene.add(rim);
    const pointer = { x: 0, y: 0 };
    const move = (event: PointerEvent) => {
      const r = element.getBoundingClientRect();
      pointer.x = (event.clientX - r.left) / r.width - 0.5;
      pointer.y = (event.clientY - r.top) / r.height - 0.5;
    };
    element.addEventListener("pointermove", move);
    const resize = () => {
      const w = element.clientWidth,
        h = element.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    resize();
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    const render = (t: number) => {
      const seconds = reduced.matches ? 0 : t * 0.001;
      camera.position.set(12 + pointer.x * 1.5, 9 + pointer.y, 14);
      camera.lookAt(0, 0, 0);
      core.rotation.y = seconds * 0.2;
      core.position.y = 1.45 + Math.sin(seconds) * 0.12;
      world.rotation.y = -0.3 + Math.sin(seconds * 0.12) * 0.08;
      renderer.render(scene, camera);
      if (!reduced.matches) frame = requestAnimationFrame(render);
    };
    render(0);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      element.removeEventListener("pointermove", move);
      geometries.forEach((g) => g.dispose());
      materials.forEach((m) => m.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);
  return (
    <div ref={host} className="hero-world" aria-hidden="true">
      <div className="world-fallback" />
    </div>
  );
}
